/**
 * Compact countdown for a file's `expires_at`. Re-renders once per minute.
 *
 * Tones:
 *   - "ok" (>3d): default neutral.
 *   - "warn" (≤3d): citrine.
 *   - "danger" (≤24h): crimson.
 */

import { useEffect, useState } from 'react';

const MS_HOUR = 60 * 60 * 1000;
const MS_DAY = 24 * MS_HOUR;

export interface ExpiryCountdownProps {
  /** ISO-8601 timestamp. */
  expiresAt: string;
}

export function ExpiryCountdown({ expiresAt }: ExpiryCountdownProps) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(t);
  }, []);
  const ms = new Date(expiresAt).getTime() - Date.now();
  const { label, tone } = formatDuration(ms);
  const colors = toneColors(tone);
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        color: colors.fg,
        fontFamily: 'var(--font-mono)',
        fontSize: 12,
      }}
    >
      <span aria-hidden style={{ width: 6, height: 6, borderRadius: 999, background: colors.dot }} />
      {label}
    </span>
  );
}

function formatDuration(ms: number): { label: string; tone: 'ok' | 'warn' | 'danger' | 'gone' } {
  if (ms <= 0) return { label: 'expired', tone: 'gone' };
  if (ms < MS_HOUR) return { label: `${Math.ceil(ms / 60000)}m`, tone: 'danger' };
  if (ms < MS_DAY) return { label: `${Math.ceil(ms / MS_HOUR)}h`, tone: 'danger' };
  const days = Math.ceil(ms / MS_DAY);
  return { label: `${days}d`, tone: days <= 3 ? 'warn' : 'ok' };
}

function toneColors(tone: 'ok' | 'warn' | 'danger' | 'gone') {
  switch (tone) {
    case 'danger':
    case 'gone':
      return { fg: 'var(--rt-error-ink)', dot: 'var(--rt-error)' };
    case 'warn':
      return { fg: 'var(--rt-miscut-ink)', dot: 'var(--rt-miscut)' };
    default:
      return { fg: 'var(--fg-2)', dot: 'var(--rt-ok)' };
  }
}
